/**
 * `entities/taxonomy-list` mutations.
 *
 * Direct hey-api client calls (these endpoints aren't in the generated SDK
 * yet). Every mutation invalidates the index; detail-scoped ones also
 * invalidate the affected detail. Toasts are surfaced here so each write gives
 * consistent feedback, mirroring `entities/taxonomy`.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { i18n } from '@/shared/i18n';
import { enqueueSnackbar } from 'notistack';
import { assetsHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { track } from '@/shared/analytics';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import type {
  AddTaxonomyListVersionPayload,
  BulkDeleteTaxonomyListsResult,
  CreateTaxonomyListPayload,
  SetTaxonomyListStatusPayload,
  TaxonomyListDetail,
  TaxonomyListSummary,
  TaxonomyListVersion,
  UpdateTaxonomyListDraftPayload,
} from '../model/types';
import { TAXONOMY_LIST_QUERY_KEYS } from './query-keys';

const LISTS_URL = '/api/v1/taxonomy-lists';

export function useCreateTaxonomyList(options?: {
  onSuccess?: (created: TaxonomyListDetail) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateTaxonomyListPayload) => {
      const response = await assetsHttp.post({ throwOnError: true, url: LISTS_URL, body: payload });
      if (!response.data) {
        throw new Error('Failed to create list');
      }
      return response.data as TaxonomyListDetail;
    },
    onSuccess: (data) => {
      // Whole slice: a version write also appends to the audit trail and can
      // move the blast radius, so detail/change-log/impact all go stale.
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.all });
      track('taxonomy_list_created', { list_id: data.id, scope: data.scope });
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyListMutations.listCreated'), {
        variant: 'success',
      });
      options?.onSuccess?.(data);
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyListMutations.failedCreateList'), {
        variant: 'error',
      });
    },
  });
}

/**
 * Seeds the org library from the shipped Standard Lists catalog.
 *
 * The catalog lives on the BACKEND — the ISO reference lists alone are ~600
 * rows, so there's nothing to send: the API is asked to seed and replies with
 * only what it actually created. Safe to call twice; the second call adds
 * nothing.
 *
 * `silent` suppresses the toast for callers that seed lists as a PREREQUISITE
 * rather than as the user's intent — the Fields tab does this so a standard
 * field can arrive bound to its governed list. Reporting "your library already
 * has every standard list" there is noise about a step the user never asked
 * for. Errors still surface: a failed prerequisite is worth interrupting for.
 */
export function useSeedStandardLists({ silent = false }: { silent?: boolean } = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    /** `keys` omitted ⇒ add everything the library is missing. */
    mutationFn: async (keys?: string[]) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${LISTS_URL}/seed-standard`,
        body: keys?.length ? { keys } : {},
      });
      return (response.data ?? []) as TaxonomyListSummary[];
    },
    onSuccess: (created) => {
      // Whole slice: a version write also appends to the audit trail and can
      // move the blast radius, so detail/change-log/impact all go stale.
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.all });
      // Tracked even when `silent` (the background seed): "how many orgs ended
      // up with the standard vocabularies" is the same question either way.
      track('taxonomy_list_seeded_from_catalog', { count: created.length });
      if (silent) return;
      enqueueSnackbar(
        created.length === 0
          ? i18n.t('knowledge:taxonomy.taxonomyListMutations.yourLibraryAlreadyHasEvery')
          : i18n.t('knowledge:taxonomy.taxonomyListMutations.addedStandardLists', {
              count: created.length,
            }),
        { variant: created.length === 0 ? 'info' : 'success' },
      );
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyListMutations.failedAddStandardLists'), {
        variant: 'error',
      });
    },
  });
}

/**
 * Copies a list, options included.
 *
 * Server-side so 250 rows don't round-trip through the browser; the copy comes
 * back as a CUSTOM list regardless of what it was cloned from.
 */
export function useDuplicateTaxonomyList(options?: {
  onSuccess?: (created: TaxonomyListDetail) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${LISTS_URL}/{id}/duplicate`,
        path: { id },
      });
      if (!response.data) {
        throw new Error('Failed to duplicate list');
      }
      return response.data as TaxonomyListDetail;
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.list() });
      enqueueSnackbar(`Created “${created.name}”`, { variant: 'success' });
      options?.onSuccess?.(created);
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyListMutations.failedDuplicateList'), {
        variant: 'error',
      });
    },
  });
}

/**
 * Deletes several lists at once. Partial: lists a schema still binds to are
 * kept and reported, so the toast can say what actually happened rather than
 * claiming a clean sweep.
 */
export function useBulkDeleteTaxonomyLists(options?: {
  onSuccess?: (result: BulkDeleteTaxonomyListsResult) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${LISTS_URL}/bulk-delete`,
        body: { ids },
      });
      if (!response.data) throw new Error('Bulk delete failed');
      return response.data as BulkDeleteTaxonomyListsResult;
    },
    onSuccess: (result) => {
      // Purge each dead list's own queries before the blanket invalidate, or
      // they refetch and 404 — same trap as the single delete.
      result.deletedIds.forEach((id) => {
        queryClient.removeQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.detail(id) });
        queryClient.removeQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.usage(id) });
        queryClient.removeQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.impact(id) });
        queryClient.removeQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.blockingSchemas(id) });
        queryClient.removeQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.changeLog(id) });
      });
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.list() });
      void queryClient.invalidateQueries({
        queryKey: TAXONOMY_LIST_QUERY_KEYS.standardCatalog(),
      });

      const n = result.deletedIds.length;
      const deleted: string = i18n.t('knowledge:taxonomy.taxonomyListMutations.deletedLists', {
        count: n,
      });
      // The blocked branch reads as dead code and is not: the client filters
      // lists that are still bound out of the selection, so nothing reachable
      // from the UI produces one. The backend still populates `blocked`, and a
      // schema that binds a list between this page loading and the delete going
      // out lands here. Deleting the branch would turn that race into a toast
      // claiming lists were deleted that were not.
      enqueueSnackbar(
        result.blocked.length > 0
          ? i18n.t('knowledge:taxonomy.taxonomyListMutations.deletedListsBlocked', {
              deleted,
              blocked: result.blocked.length,
            })
          : deleted,
        { variant: result.blocked.length > 0 ? 'warning' : 'success' },
      );
      options?.onSuccess?.(result);
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyListMutations.failedDeleteLists'), {
        variant: 'error',
      });
    },
  });
}

/** Autosave of the head draft version's columns/rows (debounced by the caller). */
export function useUpdateTaxonomyListDraft(id: string, versionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateTaxonomyListDraftPayload) => {
      const response = await assetsHttp.patch({
        url: `${LISTS_URL}/{id}/versions/{versionId}`,
        path: { id, versionId },
        body: payload,
        // `throwOnError` so the AxiosError — and with it the STATUS — reaches
        // the caller: the editor has to tell a 409 concurrent edit from a
        // network failure, and without this the client resolves with
        // `data: undefined` and the status is lost.
        throwOnError: true,
        // The concurrent-edit case is explained by a persistent banner in the
        // editor; the interceptor's own toast would repeat it once per
        // rejected autosave.
        // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
      });
      return response.data as TaxonomyListDetail;
    },
    onSuccess: (updated) => {
      // Seed the detail cache with the server's own response so the next save
      // reads the fresh `revision` — invalidation alone would race the
      // refetch, and the editor would send a stale revision and 409 itself.
      queryClient.setQueryData(TAXONOMY_LIST_QUERY_KEYS.detail(id), updated);
      // Row edits move the blast radius (usage counts, which schemas are
      // affected), so the index and the impact preview go stale. The index is
      // only marked stale: it stays mounted under the list sheet, and a refetch
      // per debounced autosave runs a query over every version in the org. The
      // list editor refetches it once it closes. The change log is not touched:
      // `updateDraftVersion` writes no entry for a draft save. Version writes DO
      // append, and they invalidate it themselves.
      void queryClient.invalidateQueries({
        queryKey: TAXONOMY_LIST_QUERY_KEYS.list(),
        refetchType: 'none',
      });
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.impact(id) });
    },
    onError: (error) => {
      // A 409 is a concurrent edit, not a failure to reach the server: the
      // editor shows a banner offering to reload, so a toast here would be a
      // second, vaguer version of the same news.
      if (parseProblemDetail(error, '').status === 409) return;
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyListMutations.failedSaveList'), {
        variant: 'error',
      });
    },
  });
}

export function useAddTaxonomyListVersion(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AddTaxonomyListVersionPayload) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${LISTS_URL}/{id}/versions`,
        path: { id },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to create version');
      }
      return response.data as TaxonomyListVersion;
    },
    onSuccess: () => {
      // Whole slice: a version write also appends to the audit trail and can
      // move the blast radius, so detail/change-log/impact all go stale.
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.all });
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyListMutations.newVersionCreated'), {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyListMutations.failedCreateVersion'), {
        variant: 'error',
      });
    },
  });
}

export function useSetTaxonomyListStatus(id: string, versionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SetTaxonomyListStatusPayload) => {
      const response = await assetsHttp.patch({
        throwOnError: true,
        url: `${LISTS_URL}/{id}/versions/{versionId}/status`,
        path: { id, versionId },
        body: payload,
      });
      return response.data as TaxonomyListVersion;
    },
    onSuccess: (version) => {
      // Whole slice: a version write also appends to the audit trail and can
      // move the blast radius, so detail/change-log/impact all go stale.
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.all });
      // `row_count` is the interesting dimension for a vocabulary: publishing a
      // 250-row list is a different act from publishing a 4-row one.
      track('taxonomy_list_status_changed', {
        list_id: id,
        status: version.status,
        row_count: version.rows.length,
      });
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyListMutations.listUpdated'), {
        variant: 'success',
      });
    },
    onError: (error) => {
      // As on the schema side: a 409 names the schema that blocks this, and the
      // interceptor already surfaces that text persistently.
      if (parseProblemDetail(error, '').status === 409) return;
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyListMutations.failedUpdateList'), {
        variant: 'error',
      });
    },
  });
}

export function useDeleteTaxonomyList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await assetsHttp.delete({ throwOnError: true, url: `${LISTS_URL}/{id}`, path: { id } });
      return id;
    },
    onSuccess: (id) => {
      // REMOVE the dead list's queries rather than invalidating them. A blanket
      // invalidate refetches anything still mounted — including this list's
      // detail/usage/impact — which 404s and surfaces a "not found" toast on
      // what was a successful delete.
      queryClient.removeQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.detail(id) });
      queryClient.removeQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.usage(id) });
      queryClient.removeQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.impact(id) });
      queryClient.removeQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.blockingSchemas(id) });
      queryClient.removeQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.changeLog(id) });
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.list() });
      // The catalog's `alreadyAdded` flags just changed — a deleted standard
      // list becomes addable again.
      void queryClient.invalidateQueries({
        queryKey: TAXONOMY_LIST_QUERY_KEYS.standardCatalog(),
      });
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyListMutations.listDeleted'), {
        variant: 'success',
      });
    },
    onError: () => {
      // The API refuses deletion while schemas still reference the list.
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyListMutations.couldNotDeleteListMay'), {
        variant: 'error',
      });
    },
  });
}
